import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { SuppressionsPage } from "../../../components/dashboard/suppressions";
import { useOneTimePageAnimation } from "@/components/motion";
import {
  createSuppressionAction,
  getSuppressionsView,
  releaseSuppressionAction,
} from "@/features/suppressions/server";
import { suppressionsQueryOptions } from "@/features/suppressions/queries";
import { capturePostHogEvent } from "@/lib/posthog-client";

export const Route = createFileRoute("/dashboard/suppressions/")({
  validateSearch: (search: Record<string, unknown>) => ({
    search: (search.search as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: "Suppressions - Bavimail" }],
  }),
  staleTime: 15_000,
  preloadStaleTime: 15_000,
  loaderDeps: ({ search }) => ({ search: search.search }),
  loader: async ({ deps, context }) => {
    await context.queryClient.ensureQueryData(suppressionsQueryOptions(deps.search));
  },
  component: DashboardSuppressionsRoute,
});

function DashboardSuppressionsRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { search } = Route.useSearch();
  const suppressionAnimationStage = useOneTimePageAnimation("dashboard-suppressions-list");
  const [searchInput, setSearchInput] = useState(search || "");
  const { data: suppressions, isFetching } = useSuspenseQuery(suppressionsQueryOptions(search));

  useEffect(() => {
    setSearchInput(search || "");
  }, [search]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const normalized = searchInput.trim() || undefined;
      if (normalized === search) return;
      void navigate({
        to: "/dashboard/suppressions",
        replace: true,
        search: { search: normalized },
      });
    }, 200);

    return () => window.clearTimeout(timeout);
  }, [navigate, search, searchInput]);

  const createMutation = useMutation({
    mutationFn: createSuppressionAction,
    onSuccess: (created) => {
      capturePostHogEvent("suppression_created");
      queryClient.setQueryData<Awaited<ReturnType<typeof getSuppressionsView>>>(
        ["suppressions", { search: search?.trim() || undefined }],
        (current) => {
          if (!current) return [created];

          const existing = current.some((suppression) => suppression.id === created.id);
          if (existing) {
            return current.map((suppression) =>
              suppression.id === created.id ? created : suppression,
            );
          }

          const filters = (search || "").trim().toLowerCase();
          if (filters) {
            const searchable =
              `${created.email} ${created.source} ${created.note || ""}`.toLowerCase();
            if (!searchable.includes(filters)) {
              return current;
            }
          }

          return [created, ...current];
        },
      );
    },
  });

  const releaseMutation = useMutation({
    mutationFn: releaseSuppressionAction,
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries({ queryKey: ["suppressions"] });
      const previous = queryClient.getQueriesData<Awaited<ReturnType<typeof getSuppressionsView>>>({
        queryKey: ["suppressions"],
      });
      queryClient.setQueriesData<Awaited<ReturnType<typeof getSuppressionsView>>>(
        { queryKey: ["suppressions"] },
        (current) =>
          current?.filter((suppression) => suppression.id !== data.suppressionId) ?? current,
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      for (const [queryKey, data] of context?.previous || []) {
        queryClient.setQueryData(queryKey as readonly unknown[], data);
      }
    },
  });

  return (
    <SuppressionsPage
      suppressions={suppressions}
      suppressionAnimationStage={suppressionAnimationStage}
      initialSearch={searchInput}
      searching={isFetching}
      onSearchChange={(nextSearch) => {
        setSearchInput(nextSearch);
      }}
      onCreate={async (input) => {
        await createMutation.mutateAsync({ data: input });
      }}
      onRelease={async (suppressionId) => {
        await releaseMutation.mutateAsync({ data: { suppressionId } });
      }}
    />
  );
}
